import { useTranslation } from 'react-i18next';

import type { LoginAuditEvent } from '@emr/shared';

import { listLoginAuditEvents } from '../../api/audit.ts';
import { Badge, type BadgeTone } from '../../components/primitives.tsx';
import type { DataTableColumn } from '../../components/DataTable.tsx';
import { useDateTimeFormat } from '../../format/dateTimeFormatContext.ts';
import { formatDateAndTime } from '../../format/formatDateTime.ts';
// Reused directly from `patients/tabs/` rather than a third parallel query→table wiring
// (`claude-kit/rules/invariants.md`'s "favor the general, reusable solution once a pattern actually
// recurs") — `ChartTable`'s own header scopes it to "the five read-only flowsheet tabs," but its
// implementation has no patient-specific content at all, and this is the first consumer outside that
// directory to need the identical fetch/loading/error/`DataTable` shape. Worth relocating to
// `components/` if a third consumer shows up; not done here to keep this change's diff to the feature
// it was asked to build.
import { ChartTable } from '../patients/tabs/ChartTable.tsx';

/**
 * The login/logout audit viewer — `GatedPages.tsx`'s `AuditPage`, on direct user instruction,
 * 2026-08-18: "we need a login / logout log". Reads `GET /api/audit/login-events`
 * (`server/src/audit/loginEventsRoutes.ts`), scoped to the caller's own org server-side.
 *
 * Deliberately narrower than the still-open general practice-wide audit-log viewer
 * (`emr-po/BACKLOG.md`): login/logout events only, not every PHI read/write. See that route's own
 * header for the full reasoning.
 *
 * **Five columns, not seven — found live, not guessed.** An earlier version had separate
 * Action/Outcome/Detail columns at `minWidthRem={60}` and passed a page-level
 * `document.documentElement.scrollWidth` check at 1440px — the same false-clean reading
 * `invariants.md`'s "table columns" section warns about, because this app's own `max-w-6xl`
 * content column plus `AppShell`'s persistent sidebar nav caps the *real* available width at
 * 872px (54.5rem) on every viewport once the sidebar mounts, which `useHasRoomForTable`'s generic
 * breakpoint formula does not account for (it only knows about `Panel`'s own padding). Measuring
 * the table's own `scrollWidth` against `<main>`'s `clientWidth` across a real width sweep
 * (1000–1600px) caught genuine page-level horizontal overflow in the ~1024–1360px band — ordinary
 * laptop widths — that the page-level check alone missed at 1440px by coincidence (enough spare
 * margin outside the centered column to absorb it at that one width). Folding Action+Outcome into
 * one `Status` badge and dropping the `fields` tag column (a minor loss — see this file's own
 * report) brings the real footprint under every sibling `ChartTable` consumer's ceiling, verified
 * clean across the same sweep afterward.
 */
export function LoginAuditLog() {
  const { t } = useTranslation('auditLog');
  const { preference } = useDateTimeFormat();

  const columns: readonly DataTableColumn<LoginAuditEvent>[] = [
    {
      key: 'occurredAt',
      header: t('columns.when'),
      cell: (event) => formatDateAndTime(event.occurredAt, preference),
      wrap: 'nowrap',
    },
    {
      key: 'actorEmail',
      header: t('columns.who'),
      cell: (event) => event.actorEmail ?? t('unknownActor'),
      wrap: 'anywhere',
      maxWidthCh: 20,
    },
    {
      key: 'status',
      header: t('columns.status'),
      cell: (event) => {
        const label = event.action === 'login' ? t('status.login') : t('status.logout');
        const tone: BadgeTone =
          event.outcome === 'denied' ? 'warning' : event.action === 'login' ? 'accent' : 'neutral';
        return (
          <Badge tone={tone}>
            {event.outcome === 'denied' ? t('status.denied', { label }) : label}
          </Badge>
        );
      },
      wrap: 'nowrap',
    },
    {
      key: 'sourceIp',
      header: t('columns.ipAddress'),
      cell: (event) => event.sourceIp ?? '—',
      wrap: 'anywhere',
      maxWidthCh: 15,
      cellClassName: 'font-mono',
    },
    {
      key: 'userAgent',
      header: t('columns.device'),
      // Genuinely unbounded — a raw browser/client `User-Agent` string, none of this app's own
      // making (`invariants.md`'s "table columns need a deliberate strategy for long or
      // variable-length content"). `wrap: 'anywhere'` plus `maxWidthCh` is that strategy; see this
      // component's own header comment above for the real-browser width sweep this was verified
      // against, and the forced-overflow falsification check in the session's own report.
      cell: (event) => event.userAgent ?? '—',
      wrap: 'anywhere',
      maxWidthCh: 22,
    },
  ];

  return (
    <ChartTable
      queryKey={['audit', 'login-events']}
      columns={columns}
      queryFn={(signal) => listLoginAuditEvents(signal)}
      rowKey={(event) => event.id}
      minWidthRem={44}
      emptyMessage={t('emptyMessage')}
      loadingLabel={t('loadingLabel')}
      caption={t('caption')}
    />
  );
}
